/**
 * A deliberately small, standalone drawer renderer. It does not depend on the
 * theme's table-based cart component, so header layout styles cannot suppress
 * the product rows.
 */
class ToeCartDrawerItems extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.refresh();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick = (event) => {
    const control = event.target.closest('[data-cart-action]');
    if (!control || control.disabled) return;

    event.preventDefault();
    const line = Number(control.dataset.cartLine);
    const item = this.cart?.items?.[line - 1];
    if (!item) return;

    const action = control.dataset.cartAction;
    const quantity = action === 'remove' ? 0 : action === 'increase' ? item.quantity + 1 : Math.max(0, item.quantity - 1);
    this.change(line, quantity, control);
  };

  async refresh() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Unable to load cart');
      this.render(await response.json());
    } catch (error) {
      console.error('Cart drawer could not load its items.', error);
    }
  }

  async change(line, quantity, control) {
    control.disabled = true;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      if (!response.ok) throw new Error('Unable to update cart');
      this.render(await response.json());
    } catch (error) {
      console.error('Cart drawer could not update this item.', error);
      control.disabled = false;
    }
  }

  render(cart) {
    this.cart = cart;
    this.innerHTML = `<form action="${this.escape(window.Shopify?.routes?.root || '/') }cart" class="cart-form toe-cart-drawer__form" id="cart-form" method="post"><div class="toe-cart-drawer__list">${cart.items.length ? cart.items.map((item, index) => this.itemMarkup(item, index + 1)).join('') : '<p class="toe-cart-drawer__empty">Your cart is empty.</p>'}</div></form>`;
    this.updateCartChrome(cart);
  }

  itemMarkup(item, line) {
    const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<p class="toe-cart-drawer__variant">${this.escape(item.variant_title)}</p>` : '';
    const image = item.image ? `<img src="${this.escape(item.image)}" alt="${this.escape(item.product_title)}" width="176" height="176" loading="eager">` : '';
    return `<article class="toe-cart-drawer__line" data-cart-line="${line}"><a class="toe-cart-drawer__media" href="${this.escape(item.url)}">${image}</a><div class="toe-cart-drawer__details"><a class="toe-cart-drawer__title" href="${this.escape(item.url)}">${this.escape(item.product_title)}</a>${variant}<span class="toe-cart-drawer__price">${this.money(item.final_price, item.currency)}</span><div class="toe-cart-drawer__controls"><button class="toe-cart-drawer__quantity-button" type="button" data-cart-action="decrease" data-cart-line="${line}" aria-label="Decrease quantity for ${this.escape(item.product_title)}">−</button><span class="toe-cart-drawer__quantity" aria-label="Quantity">${item.quantity}</span><button class="toe-cart-drawer__quantity-button" type="button" data-cart-action="increase" data-cart-line="${line}" aria-label="Increase quantity for ${this.escape(item.product_title)}">+</button><button class="toe-cart-drawer__remove" type="button" data-cart-action="remove" data-cart-line="${line}">Remove</button></div></div></article>`;
  }

  updateCartChrome(cart) {
    const total = this.money(cart.total_price, cart.currency);
    document.querySelectorAll('[data-cart-subtotal]').forEach((element) => {
      element.textContent = total;
      element.setAttribute('value', total);
    });
    const drawer = this.closest('cart-drawer-component');
    drawer?.querySelectorAll('.cart-bubble__text').forEach((element) => { element.textContent = String(cart.item_count); });
    document.querySelectorAll('cart-icon').forEach((icon) => {
      if (typeof icon.renderCartBubble === 'function') icon.renderCartBubble(cart.item_count, false, false);
    });
    document.querySelectorAll('.cart__checkout-button').forEach((button) => { button.disabled = cart.item_count === 0; });
  }

  money(cents, currency) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: currency || this.dataset.currency || 'USD' }).format(cents / 100);
  }

  escape(value) {
    return String(value ?? '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[character]);
  }
}

if (!customElements.get('toe-cart-drawer-items')) {
  customElements.define('toe-cart-drawer-items', ToeCartDrawerItems);
}
